import { getConfig } from '@klpkonsern/appconfig';

import {
    RegistrationStatuses,
    useGetRegistrationStatusQuery,
} from '../services/rtkApi';
import { useCurrentUser } from '@klp/auth';
import type { IContractToRegistrationProcessActivity } from '../classes/ContractToRegistrationProcessResponse';
import type { Bruker } from '@klp/user/types';
import i18n from '@forvalter/i18n/config';
import { useCallback } from 'react';
import type { FormField } from '@klp/formgenerator/interfaces';
import {
    convertFormFieldstoFormValues,
    findCamundaValidation,
    formfieldIsHidden,
} from '@klp/formgenerator/utils';

const useRegistrationRoles = () => {
    const { currentUser } = useCurrentUser();

    let registrationStatus: Array<string> = [];

    if (currentUser?.isForvalter) {
        registrationStatus = [
            RegistrationStatuses.NY,
            RegistrationStatuses.GARANTI_REGISTRERING,
            RegistrationStatuses.HOS_FORVALTER,
            RegistrationStatuses.TIL_GODKJENNING,
        ];
    }

    if (currentUser?.isOkonomi || currentUser?.isQAOkonomi) {
        registrationStatus = [
            RegistrationStatuses.TILDELT_OKONOMI,
            RegistrationStatuses.REGISTRERING_D365,
            RegistrationStatuses.GODKJENT,
            RegistrationStatuses.QA_UTFORT,
            RegistrationStatuses.TIL_QA,
        ];
    }

    return { registrationStatus };
};

const useFormattedDate = (date?: string | null) => {
    if (!date) return;

    return new Date(date)?.toLocaleDateString('nb-NO', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });
};

const getDynamics365URL = () => getConfig('WEBAPP_APP_DYNAMICS365_LINK');

type Statuses = keyof typeof RegistrationStatuses;
type RegistrationStatusesMap = Partial<Record<RegistrationStatuses, Statuses>>;

const useGetRegistrationStatusesMap = () => {
    const { data: statuses = [] } = useGetRegistrationStatusQuery();

    return Object.entries(RegistrationStatuses).reduce((acc, [key, value]) => {
        if (statuses.includes(value)) {
            acc[key as keyof typeof RegistrationStatuses] = value;
        }
        return acc;
    }, {} as RegistrationStatusesMap);
};

const useRoleTranslationMapping = () => {
    const handleMapping = useCallback((role?: string) => {
        let roleMapping = '';
        switch (role) {
            case 'Forvalter':
                roleMapping = i18n.t('checkOffContract.roles.Forvalter');
                return roleMapping;
            case 'Okonomi':
                roleMapping = i18n.t('checkOffContract.roles.Okonomi');
                return roleMapping;

            case 'QAOkonomi':
                roleMapping = i18n.t('checkOffContract.roles.QAOkonomi');
                return roleMapping;

            default:
                return role;
        }
    }, []);

    return { handleMapping };
};

const getRegistreringStatusTranslations = (value: string) => {
    let text = '';
    switch (value) {
        case 'IkkeAvsjekket':
            return 'contractRegistration.statuses.notCheckedOff';
        case 'Avsjekket':
            return 'contractRegistration.statuses.checkedOff';
        case RegistrationStatuses.NY:
            return 'contractRegistration.statuses.new';
        case RegistrationStatuses.SKAL_IKKE_REGISTRERES:
            return 'contractRegistration.statuses.shallNotBeRegistered';
        case RegistrationStatuses.HOS_FORVALTER:
            return 'contractRegistration.statuses.withForvalter';
        case RegistrationStatuses.TILDELT_OKONOMI:
            return 'contractRegistration.statuses.assignedFinance';
        case RegistrationStatuses.REGISTRERING_D365:
            return 'contractRegistration.statuses.registrationD365';
        case RegistrationStatuses.TIL_QA:
            return 'contractRegistration.statuses.toQA';
        case RegistrationStatuses.QA_UTFORT:
            return 'contractRegistration.statuses.QACompleted';
        case RegistrationStatuses.TIL_GODKJENNING:
            return 'contractRegistration.statuses.forApporval';
        case RegistrationStatuses.GODKJENT:
            return 'contractRegistration.statuses.approved';
        case RegistrationStatuses.FULLFORT:
            return 'contractRegistration.statuses.completed';
        case RegistrationStatuses.GARANTI_REGISTRERING:
            return 'contractRegistration.statuses.RegistreringAvGaranti';
        default:
            text = '';
    }
    return text;
};

const mapUserRolesToCandidateGroup = (
    activity: IContractToRegistrationProcessActivity,
    user?: Bruker,
) => {
    if (!user) {
        return {
            isUserCandidateForActivity: false,
            requiredRoles: [],
        };
    }

    const roleMapping = {
        isForvalterTask: user.isForvalter,
        isOkonomiTask: user.isOkonomi,
        isQAOkonomiTask: user.isQAOkonomi,
    };

    const isUserCandidateForActivity = Object.entries(roleMapping).some(
        ([activityKey, userHasRole]) =>
            (activity?.[activityKey as keyof typeof roleMapping] &&
                userHasRole) ||
            user.isEDAdmin,
    );

    const requiredRoles = Object.entries(activity || {})
        .filter(([key, value]) => key.endsWith('Task') && value === true)
        .map(([key]) => key.replace('is', '').replace('Task', ''));

    return {
        isUserCandidateForActivity,
        requiredRoles,
    };
};

const useMapUserRolesToCandidateGroup = (
    activity?: IContractToRegistrationProcessActivity,
) => {
    const { currentUser } = useCurrentUser();

    if (!currentUser || !activity) {
        return {
            isUserCandidateForActivity: false,
            requiredRoles: [],
        };
    }

    return mapUserRolesToCandidateGroup(activity, currentUser);
};

const getActivityIsDisabled = (
    activity: IContractToRegistrationProcessActivity,
    currentUser?: Bruker,
) => {
    if (!activity.isVisible) return true;

    const activityIsNotStarted = activity.id === null;
    if (activityIsNotStarted) return true;

    const activityCompleted =
        activity.id !== null && activity.utfortDato !== null;
    if (activityCompleted) return true;

    if (currentUser?.isEDAdmin) return false;

    const { isUserCandidateForActivity } = mapUserRolesToCandidateGroup(
        activity,
        currentUser,
    );

    return !isUserCandidateForActivity;
};

const handleRequiredFormFields = (formFields?: Array<FormField>) => {
    if (formFields?.length === 0 || !formFields) {
        return false;
    }

    const convartedFormFields = convertFormFieldstoFormValues(formFields);
    const hasUnfilledRequiredField = formFields.some((field) => {
        const isHidden = formfieldIsHidden(field, convartedFormFields);
        const isRequired = findCamundaValidation('required', field.validation);
        const value = field.value;

        const isEmpty =
            value === undefined ||
            value === null ||
            value === '' ||
            value === '0';

        return isRequired && !isHidden && isEmpty;
    });

    return hasUnfilledRequiredField;
};

enum contractregistrationLocalStorageReferences {
    SELECTED_STATUS = 'storedSelectedStatus',
}

export {
    useRegistrationRoles,
    useFormattedDate,
    getDynamics365URL,
    useGetRegistrationStatusesMap,
    mapUserRolesToCandidateGroup,
    useMapUserRolesToCandidateGroup,
    useRoleTranslationMapping,
    handleRequiredFormFields,
    contractregistrationLocalStorageReferences,
    getActivityIsDisabled,
    getRegistreringStatusTranslations,
};
